/*    
<summary>
   This class component is all about Managing Category data functionality.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import toast from "react-hot-toast";
import { IObservableInitialState } from "../../models/ICommon";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState as GetAllCategoryManagementState } from "../initialState/get-all-category-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";

import URLConstants from "../../constants/url-constants";

import { transformCategoryData, transformCategoryDataProduct } from "../../helpers/category-helper";
import { IAddCategoryManagement } from "../../models/forms/IAddCategory";
import {
  ICategoryManagementListVM,
  ICategoryManagementVM,
  ICategoryManagementVMStore
} from "../../models/response/ICategoryManagementResposne";
import { ICategory } from "../../models/state/IStoreTreatment";
import { ICategoryFilter } from "../../models/IFilter";
import { categoryInitialFilterState } from "../initialState/initial-filter-state";
import { getBusinessTypeIdFromLocalStorage, getStoreIdFromLocalStorage, getUserTypeFromLocalStorage } from "../../helpers/localstorage-helper";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { BookingManagementStore } from "./booking-management-store";

const authStore = new AuthStore();

export class CategoryManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  categoryList: ICategoryManagementListVM = GetAllCategoryManagementState;
  categoryListForStore: ICategoryManagementVMStore[] = [];
  categoryListForStoreProduct: ICategoryManagementVMStore[] = [];
  categoryListForStoreAdd: ICategoryManagementVMStore[] = [];
  categoryListForStoreAddProduct: ICategoryManagementVMStore[] = [];

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateCategoryManagementState = { ...this.initialStateValue };
  addUpdateStoreCategoryOrderState = { ...this.initialStateValue };
  deleteCategoryManagementState = { ...this.initialStateValue };
  allCategoryState = { ...this.initialStateValue };

  activeInactiveState = { ...this.initialStateValue };

  category: ICategoryManagementVM | undefined = undefined;
  categoryState = { ...this.initialStateValue };

  categoryFilter: ICategoryFilter = {
    ...categoryInitialFilterState
  }

   getTreatmentOptionMenuListState = {
    inProgress: false,
    error: null,
  };

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      categoryList: observable,
      category: observable,
      getTreatmentOptionMenuListState: observable,
      allCategoryState: observable,
      addUpdateCategoryManagementState: observable,
      deleteCategoryManagementState: observable,
      addUpdateStoreCategoryOrderState: observable,
      categoryListForStoreAddProduct: observable,
      categoryListForStore: observable,
      categoryListForStoreProduct: observable,
      categoryState: observable,
      categoryListForStoreAdd: observable,
      categoryFilter: observable,
      GetCategoryManagementListService: action,
      GetCategoryListByStoreIdForProductService: action,
      GetCategoryManagementListFilterService: action,
      setCategoryFilterDetail: action,
      GetCategoryListByStoreIdService: action,
      GetAllCategoriesService: action,
      AddCategoryManagementService: action,
      UpdateCategoryManagementService: action,
      UpdateCategoryOrderIdService: action,
      GetSearchCategoryFilterService: action,
      UpdateCategoryManagementStatusService: action,
      DeleteCategoryManagementService: action,
      // fetchTreatmentOptionsForAllCategories: action,
      GetCategoryManagementByIdService: action,
      GetAllCategoriesServiceReservation: action,
      reset: action,
      resetCategory: action,
      resetCategoryList: action,
      resetDeleteCategoryManagement: action,
      resetCategoryManagementDetail: action,
      resetAddUpdateStoreCategoryOrderState: action,
      resetAddUpdateCategoryManagementState: action,
      GetCategorySummaryListByStoreIdProductService: action,
      GetCategorySummaryListByStoreIdService: action,
      resetCategoryFilterDetail: action,
      UpdateStoreCategoryOrderIdService: action,
      allCategoryManagement: computed,
      allCategoryBookingManagement: computed,
      categoryDetail: computed,
      allCategories: computed,
      allCategoryForTreatmentStore: computed,
      allCategoryForProductStore: computed,
      allCategoryManagementStore: computed,
      allCategoryManagementStoreForAdd: computed,
      allCategoryManagementStoreForAddProduct: computed,
      getCategoryFilter: computed,
    });
  }

  /**
   * This function is used to Get All StaffManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetCategoryManagementListService = (
    currentPage: number,
    pagerSize: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.CategoryManagementList +
      "?PageNo=" +
      currentPage +
      "&PageSize=" +
      pagerSize;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.categoryList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * Fetches the list of product registrations based on the provided search query and pagination details.
   * @param searchQuery - Search query string to filter product registrations
   */

  GetSearchCategoryFilterService = (
    searchQuery: string,
    distinction: number,
    businessType: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.GetCategorySearchFilter +
      "?Search=" +
      searchQuery +
      "&Distinction=" +
      distinction +
      "&BusinessType=" +
      businessType;
    encodeURIComponent(searchQuery);

    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.categoryList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Get All StaffManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetCategoryManagementListFilterService = (
    businessType: number,
    distinction: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.CategoryManagementFilter +
      "?buisnessType=" +
      businessType +
      "&Distinction=" +
      distinction;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.categoryList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * This function is used to map staffList to allStaffManagement List suitable for Grid Component.
   * @returns allStaffManagementsList
   */
  get allCategoryManagement() {
    if (
      this.categoryList?.CategoryVM &&
      this.categoryList.CategoryVM.length > 0
    ) {
      const map = new Map<
        number,
        {
          id: number;
          title: string;
          label: string;
          key: number;
          children: any[];
          orderId: number;
          parentOrgId: number;
          desc: string;
        }
      >();

      // Create nodes and map by their ID
      this.categoryList.CategoryVM.forEach(
        (item: {
          Id: number;
          CategoryName: string;
          OrderId: number;
          ParentCategoryId: number;
          Description: string;
        }) => {
          map.set(item.Id, {
            id: item.Id,
            title: item.CategoryName,
            label: item.CategoryName,
            parentOrgId: item.ParentCategoryId,
            key: item.Id,
            children: [],
            orderId: item.OrderId,
            desc: item.Description,
          });
        }
      );

      // Build the tree structure
      return this.categoryList.CategoryVM.reduce(
        (tree: any[], item: { Id: number; ParentCategoryId: number }) => {
          if (item.ParentCategoryId === 0) {
            // Top-level node
            tree.push(map.get(item.Id));
          } else {
            // Add as child of its parent
            const parent = map.get(item.ParentCategoryId);
            if (parent) {
              parent.children.push(map.get(item.Id));

              // Sort children by orderId after addition
              parent.children.sort((a, b) => a.orderId - b.orderId);
            }
          }
          return tree;
        },
        []
      );
    }

    return [];
  }

  GetCategorySummaryListByStoreIdService = (
    distinction: number,
    storeId: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.GetTreatmentRegistartionStoreId +
      "?StoreId=" +
      storeId +
      "&Distinction=" +
      distinction;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.categoryListForStoreAdd = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetCategorySummaryListByStoreIdProductService = (
    distinction: number,
    storeId: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.GetTreatmentRegistartionStoreId +
      "?StoreId=" +
      storeId +
      "&Distinction=" +
      distinction;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.categoryListForStoreAddProduct = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetCategoryListByStoreIdService = (
    distinction: number,
    storeId: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.GetTreatmentRegistartionStoreId +
      "?StoreId=" +
      storeId +
      "&Distinction=" +
      distinction;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.categoryListForStore = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetCategoryListByStoreIdForProductService = (
    distinction: number,
    storeId: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.GetTreatmentRegistartionStoreId +
      "?StoreId=" +
      storeId +
      "&Distinction=" +
      distinction;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.categoryListForStoreProduct = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  get allCategoryManagementStore() {
    if (
      this.categoryListForStore &&
      this.categoryListForStore.length > 0
    ) {
      const map = new Map<
        number,
        {
          id: number;
          title: string;
          label: string;
          key: number;
          children: any[];
          orderId: number;
          parentOrgId: number;
          desc: string;
        }
      >();

      // Create nodes and map by their ID
      this.categoryListForStore.forEach(
        (item: any) => {
          map.set(item.Id, {
            id: item.Id,
            title: item.CategoryName,
            label: item.CategoryName,
            parentOrgId: item.ParentCategoryId,
            key: item.Id,
            children: [],
            orderId: item.OrderId,
            desc: item.Description,
          });
        }
      );

      // Build the tree structure
      return this.categoryListForStore.reduce(
        (tree: any[], item: { Id: number; ParentCategoryId: number }) => {
          if (item.ParentCategoryId === 0) {
            // Top-level node
            tree.push(map.get(item.Id));
          } else {
            // Add as child of its parent
            const parent = map.get(item.ParentCategoryId);
            if (parent) {
              parent.children.push(map.get(item.Id));

              // Sort children by orderId after addition
              parent.children.sort((a, b) => a.orderId - b.orderId);
            }
          }
          return tree;
        },
        []
      );
    }

    return [];
  }

  get allCategoryManagementStoreForAdd() {
    if (
      this.categoryListForStoreAdd &&
      this.categoryListForStoreAdd.length > 0
    ) {
      const map = new Map<
        number,
        {
          id: number;
          title: string;
          label: string;
          key: number;
          children: any[];
          orderId: number;
          parentOrgId: number;
          desc: string;
        }
      >();

      // Create nodes and map by their ID
      this.categoryListForStoreAdd.forEach(
        (item: any) => {
          map.set(item.CategoryId, {
            id: Number(item.CategoryId),
            title: item.CategoryName,
            label: item.CategoryName,
            parentOrgId: Number(item.ParentCategoryId),
            key: Number(item.CategoryId),
            children: [],
            orderId: item.OrderId,
            desc: item.Description,
          });
        }
      );

      // Build the tree structure
      return this.categoryListForStoreAdd.reduce(
        (tree: any[], item: { CategoryId: number; ParentCategoryId: number }) => {
          if (item.ParentCategoryId === 0) {
            // Top-level node
            tree.push(map.get(item.CategoryId));
          } else {
            // Add as child of its parent
            const parent = map.get(item.ParentCategoryId);
            if (parent) {
              parent.children.push(map.get(item.CategoryId));

              // Sort children by orderId after addition
              parent.children.sort((a, b) => a.orderId - b.orderId);
            }
          }
          return tree;
        },
        []
      );
    }

    return [];
  }

  get allCategoryManagementStoreForAddProduct() {
    if (
      this.categoryListForStoreAddProduct &&
      this.categoryListForStoreAddProduct.length > 0
    ) {
      const map = new Map<
        number,
        {
          id: number;
          title: string;
          label: string;
          key: number;
          children: any[];
          orderId: number;
          parentOrgId: number;
          desc: string;
        }
      >();

      // Create nodes and map by their ID
      this.categoryListForStoreAddProduct.forEach(
        (item: any) => {
          map.set(item.CategoryId, {
            id: Number(item.CategoryId),
            title: item.CategoryName,
            label: item.CategoryName,
            parentOrgId: Number(item.ParentCategoryId),
            key: Number(item.CategoryId),
            children: [],
            orderId: item.OrderId,
            desc: item.Description,
          });
        }
      );

      // Build the tree structure
      return this.categoryListForStoreAddProduct.reduce(
        (tree: any[], item: { CategoryId: number; ParentCategoryId: number }) => {
          if (item.ParentCategoryId === 0) {
            // Top-level node
            tree.push(map.get(item.CategoryId));
          } else {
            // Add as child of its parent
            const parent = map.get(item.ParentCategoryId);
            if (parent) {
              parent.children.push(map.get(item.CategoryId));

              // Sort children by orderId after addition
              parent.children.sort((a, b) => a.orderId - b.orderId);
            }
          }
          return tree;
        },
        []
      );
    }

    return [];
  }

  get allCategoryForTreatmentStore() {
    if (
      this.categoryListForStore &&
      this.categoryListForStore.length > 0
    ) {
      let data = this.categoryListForStore.map(
        (category) => {
          return {
            Id: category.Id,
            CategoryId: category.CategoryId,
            CategoryName: category.CategoryName, // Name of the category
            Distinction: category.Distinction, // Specifies the type of distinction
            BusinessTypeId: category.BusinessTypeId, // Selected business type
            ParentCategoryId: category.ParentCategoryId, // Parent category associated with the category
            OrderId: category.OrderId,
          }
        }
      )
      let finalData = transformCategoryData(data);
      return finalData;
    }
    return [];
  }

  get allCategoryForProductStore() {
    if (
      this.categoryListForStoreProduct &&
      this.categoryListForStoreProduct.length > 0
    ) {
      let data = this.categoryListForStoreProduct.map(
        (category) => {
          return {
            Id: category.Id,
            CategoryId: category.CategoryId,
            CategoryName: category.CategoryName, // Name of the category
            Distinction: category.Distinction, // Specifies the type of distinction
            BusinessTypeId: category.BusinessTypeId, // Selected business type
            ParentCategoryId: category.ParentCategoryId, // Parent category associated with the category
            OrderId: category.OrderId,
          }
        }
      )
      let finalData = transformCategoryDataProduct(data);
      return finalData;
    }
    return [];
  }

  /**
   * This function is used to Get StaffManagement Details by Id by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  GetCategoryManagementByIdService = (id: number) => {
    this.categoryState.inProgress = true;
    let url = `${URLConstants.GetCategoryById}/${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.categoryState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.category = data;
        }
      })
      .catch((err: string) => {
        this.categoryState.error = err;
      })
      .finally(
        action(() => {
          this.categoryState.inProgress = false;
        })
      );
  };

  get allCategoryBookingManagement() {
    if (
      this.categoryList?.CategoryVM &&
      this.categoryList.CategoryVM.length > 0
    ) {
      const map = new Map<
        number,
        {
          id: number;
          title: string;
          label: string;
          key: number;
          children: any[];
          orderId: number;
          parentOrgId: number;
          desc: string;
          distinction: number;
        }
      >();

      // Create nodes and map by their ID
      this.categoryList.CategoryVM.forEach(
        (item: {
          Id: number;
          CategoryName: string;
          OrderId: number;
          ParentCategoryId: number;
          Description: string;
          Distinction: number;
        }) => {
          map.set(item.Id, {
            id: item.Id,
            title: item.CategoryName,
            label: item.CategoryName,
            parentOrgId: item.ParentCategoryId,
            key: item.Id,
            children: [],
            orderId: item.OrderId,
            desc: item.Description,
            distinction: item.Distinction
          });
        }
      );

      // Build the tree structure
      return this.categoryList.CategoryVM.reduce(
        (tree: any[], item: { Id: number; ParentCategoryId: number }) => {
          if (item.ParentCategoryId === 0) {
            // Top-level node
            tree.push(map.get(item.Id));
          } else {
            // Add as child of its parent
            const parent = map.get(item.ParentCategoryId);
            if (parent) {
              parent.children.push(map.get(item.Id));

              // Sort children by orderId after addition
              parent.children.sort((a, b) => a.orderId - b.orderId);
            }
          }
          return tree;
        },
        []
      );
    }

    return [];
  }

  /**
   * This function provides initail values to the Add StaffManagement Form.
   * @returns StaffManagement Details
   *
   */
  get categoryDetail(): any {
    if (this.category) {
      return {
        Category: this.category.CategoryName, // Assuming `CategoryName` maps to the category name
        Distinction: this.category.Distinction, // Defaulting to "treatment" if not provided
        BusinessType: this.category.BusinessType, // Business type of the category
        ParentCategoryId: this.category.ParentCategoryId, // Parent category
        OrderId: this.category.OrderId,
        Details: this.category.Details,
        Description: this.category.Description,
      };
    }
    return {
      Category: "",
    };
  }

  /*
    This function is used to reset all staff observables to their initial values.  
    */
  resetCategoryManagementDetail = () => {
    this.category = undefined;
    this.categoryState = { ...this.initialStateValue };
  };

  /**
   * This function is used to Add New StaffManagement by calling an API.
   * @param data : StaffManagement Data
   * @returns
   */
  AddCategoryManagementService = (data: IAddCategoryManagement) => {
    this.addUpdateCategoryManagementState.inProgress = true;
    let url = URLConstants.AddCategoryManagement;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateCategoryManagementState.error = response.data.Message;
        } else this.addUpdateCategoryManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateCategoryManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCategoryManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing StaffManagement Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param data
   * @returns
   */
  UpdateCategoryManagementService = (data: IAddCategoryManagement) => {
    this.addUpdateCategoryManagementState.inProgress = true;
    let url = URLConstants.UpdateCategoryManagement;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateCategoryManagementState.error = response.data.Message;
        } else this.addUpdateCategoryManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateCategoryManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCategoryManagementState.inProgress = false;
        })
      );
  };

  UpdateCategoryOrderIdService = (data: any) => {
    this.addUpdateCategoryManagementState.inProgress = true;
    let url = URLConstants.UpdateCategoryOrderId;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateCategoryManagementState.error = response.data.Message;
        } else this.addUpdateCategoryManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateCategoryManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateCategoryManagementState.inProgress = false;
        })
      );
  };

  UpdateStoreCategoryOrderIdService = (data: any, storeId: number, distinction: number) => {
    this.addUpdateStoreCategoryOrderState.inProgress = true;
    let url = URLConstants.UpdateStoreCategoryOrderId;
    let mappedData = data.map((item: any) => {
      return {
        // Id: Number(item.parentId),
        Id: Number(item.id),
        StoreId: storeId,
        CategoryId: Number(item.categoryId),
        OrderId: item.orderId,
        ParentCategoryId: Number(item.ParentCategoryId),
        Distinction: distinction
      };
    })
    return baseService
      .putRequest(url, { Categories: mappedData })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStoreCategoryOrderState.error = response.data.Message;
        } else { this.addUpdateStoreCategoryOrderState.success = true };
      })
      .catch((err: string) => {
        this.addUpdateStoreCategoryOrderState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStoreCategoryOrderState.inProgress = false;
        })
      );
  };

  resetAddUpdateStoreCategoryOrderState = () => {
    this.addUpdateStoreCategoryOrderState.inProgress = false;
    this.addUpdateStoreCategoryOrderState.success = false;
    this.addUpdateStoreCategoryOrderState.error = "";
  };

  /**
   * This function is used to Delete StaffManagement by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  DeleteCategoryManagementService = (id: number) => {
    this.deleteCategoryManagementState.inProgress = true;
    let url = `${URLConstants.DeleteCategoryManagement}/${id}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteCategoryManagementState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteCategoryManagementState.success = true;
        }
      })
      .catch((err: string) => {
        this.deleteCategoryManagementState.error = err;
        // toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.deleteCategoryManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetAddUpdateCategoryManagementState = () => {
    this.addUpdateCategoryManagementState.inProgress = false;
    this.addUpdateCategoryManagementState.success = false;
    this.addUpdateCategoryManagementState.error = "";
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetDeleteCategoryManagement = () => {
    this.deleteCategoryManagementState.inProgress = false;
    this.deleteCategoryManagementState.success = false;
    this.deleteCategoryManagementState.error = "";
  };

  /**
   * This function is used to Updating Existing StaffManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param state: StaffManagement state
   * @returns
   */
  UpdateCategoryManagementStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.CategoryManagementStatus;
    let categoryData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, categoryData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };

  resetCategoryList = () => {
    this.categoryList = GetAllCategoryManagementState;
    this.categoryListForStore = [];
  };

  resetCategoryStoreListTreatment = () => {
    this.categoryListForStore = [];
  };

  resetCategoryStoreListProduct = () => {
    this.categoryListForStoreProduct = [];
  };


  /**
 * This function is used to get all the categories by calling an API
 * @returns
 */
  GetAllCategoriesService = () => {
    this.allCategoryState.inProgress = true;
    let url = URLConstants.GetAllCategory;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.allCategoryState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.categoryList.CategoryVM = data;
        }
      })
      .catch((err: string) => {
        this.allCategoryState.error = err;
      })
      .finally(
        action(() => {
          this.allCategoryState.inProgress = false;
        })
      );
  };

  GetAllCategoriesServiceReservation = (businessType: any) => {
    this.allCategoryState.inProgress = true;
    const urlParams = new URLSearchParams();
    let userType = getUserTypeFromLocalStorage();
    let storeId = getStoreIdFromLocalStorage();
    if (businessType > 0) {
      urlParams.append("businessTypeId", String(businessType));
    }

    if (storeId && storeId !== -1) {
      urlParams.append("storeId", String(storeId));
    }
    let url = URLConstants.GetAllCategory + "?" + urlParams.toString();
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.allCategoryState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.categoryList.CategoryVM = data;
        }

        // this.fetchTreatmentOptionsForAllCategories(this.categoryList?.CategoryVM);
      })
      .catch((err: string) => {
        this.allCategoryState.error = err;
      })
      .finally(
        action(() => {
          this.allCategoryState.inProgress = false;
        })
      );
  };



  get allCategories() {
    if (this.categoryList?.CategoryVM && this.categoryList.CategoryVM.length > 0) {
      return this.categoryList.CategoryVM?.map((category: any) => {
        return {
          Id: category.Id,
          CategoryName: category.CategoryName,
          BusinessTypeId: category.BusinessTypeId,
          Distinction: category.Distinction,
          OrderId: category.OrderId,
          ParentCategoryId: category.ParentCategoryId,
          Details: category.Details
        }
      })
    }
    return [];
  }

  getAllChildIds = (categories: ICategoryManagementVM[], parentId: number): number[] =>{
    const result: number[] = [];

    function findChildren(pid: number) {
      const directChildren = categories.filter(c => c.ParentCategoryId === pid);
      for (const child of directChildren) {
        result.push(child.Id);
        findChildren(child.Id); // recursive for sub-children
      }
    }

    findChildren(parentId);
    return result;
    }



  setCategoryFilterDetail = (filter: ICategoryFilter) => {
    this.categoryFilter = { ...filter };
  };

  resetCategoryFilterDetail = () => {
    this.categoryFilter = { ...categoryInitialFilterState };
  };

  get getCategoryFilter(): ICategoryFilter {
    return this.categoryFilter;
  }

  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateCategoryManagementState.success = false;
    this.addUpdateCategoryManagementState.error = "";
    this.deleteCategoryManagementState = { ...this.initialStateValue };
    this.addUpdateCategoryManagementState.inProgress = false;
    this.categoryState = { ...this.initialStateValue };
    this.categoryFilter = { ...categoryInitialFilterState };
  };

  /**
   * This function is used to reset all staff observables to their initial values.
   * @returns
   */
  resetCategory = () => {
    this.error = "";
    this.inProgress = false;
    this.categoryList = GetAllCategoryManagementState;

    this.addUpdateCategoryManagementState = { ...this.initialStateValue };
    this.deleteCategoryManagementState = { ...this.initialStateValue };
    this.category = undefined;
    this.categoryState = { ...this.initialStateValue };
  };
}

export default new CategoryManagementStore();
